/**
 * VireoHR - Login Screen
 * Email + Password Authentication
 * Styled with Gosta design system
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import Input from '@/components/Input';
import { useAuth } from '@/contexts/AuthContext';

export default function Login() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      redirectByRole(user.role);
    }
  }, [user]);

  const redirectByRole = (role: string) => {
    switch (role) {
      case 'admin':
        navigate('/admin/tenants', { replace: true });
        break;
      case 'owner':
        navigate('/owner/home', { replace: true });
        break;
      case 'manager':
        navigate('/manager/home', { replace: true });
        break;
      case 'employee':
        navigate('/employee/home', { replace: true });
        break;
      default:
        navigate('/login', { replace: true });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      // User will be redirected by useEffect when user state updates
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#0D9488] flex items-center justify-center ">
            <span className="text-4xl font-bold text-[#D4AF37]">V</span>
          </div>
          <h1 className="text-3xl font-bold text-[#0D9488] mb-2">VireoHR</h1>
          <p className="text-[#D4AF37]">Workforce management simplified</p>
        </div>

        {/* Login Card */}
        <Card>
          <h2 className="text-2xl font-bold text-[#0D9488] mb-6 text-center">
            Sign In
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />

            {error && (
              <div className="p-3 bg-[#DC2626] bg-opacity-10 border border-[#DC2626] rounded-lg">
                <p className="text-sm text-[#DC2626] font-semibold">❌ {error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={isLoading || !email || !password}
              className="w-full"
            >
              {isLoading ? '⏳ Signing in...' : '🔐 Sign In'}
            </Button>
          </form>
        </Card>

        {/* Signup CTA */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500 mb-2">
            New to VireoHR?
          </p>
          <button
            onClick={() => navigate('/signup')}
            className="text-sm text-[#0D9488] hover:underline hover:opacity-80 transition-opacity"
          >
            Create a new business account
          </button>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-[#D4AF37] mt-6">
          © 2024 VireoHR. All rights reserved.
        </p>
      </div>
    </div>
  );
}
